/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import { Button, IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { subscribeAction, SubscribeDataInput } from '../../dataActions/subscribe-data.action';
import { IAaa_subscribeConfig, IAaa_subscribeProps } from './aaa_subscribe.props.autogenerated';

interface ISubscribeState {
    email: string;
}

/**
 * Subscribe view props.
 */
export interface ISubscribeViewProps extends IAaa_subscribeProps<IAaa_subscribeConfig>, ISubscribeState {
    subscribe: IModuleProps;
    subscribeContainer: INodeProps;
    heading: React.ReactNode;
    text: React.ReactNode;
    submitButton?: INodeProps;
    submitButtonLabelText?: string;
    subscribeForm?: INodeProps;
    emailInput?: INodeProps;
    handleText(): void;
}

/**
 * Subscribe component.
 * @extends {React.PureComponent<IAaa_subscribeProps<IAaa_subscribeData>>}
 */
export class Subscribe extends React.PureComponent<IAaa_subscribeProps<IAaa_subscribeConfig>, ISubscribeState> {
    private readonly _emailAddressInputRef: React.RefObject<HTMLInputElement>;

    public constructor(props: ISubscribeViewProps) {
        super(props);
        this._emailAddressInputRef = React.createRef();
        this._buttonSubscribe = this._buttonSubscribe.bind(this);
    }

    public state: ISubscribeState = {
        email: ''
    };

    public render(): JSX.Element | null {
        const { heading, paragraph } = this.props.config;

        const moduleClassName = 'ms-subscribe';

        const subscribeParagraph = paragraph && (
            <Msdyn365.RichTextComponent
                text={paragraph}
                className='ms-subscribe__details__text'
                editProps={{ onEdit: this.handleParagraphChange, requestContext: this.props.context.request }}
            />
        );

        const subscribeHeading = heading && (
            <Msdyn365.Text
                className='ms-subscribe__details__heading'
                tag={heading.tag ?? 'h1'}
                text={heading.text}
                editProps={{ onEdit: this.handleTextChange, requestContext: this.props.context.request }}
            />
        );
        const viewProps = {
            ...this.props,
            handleText: this.handleTextChange,
            subscribe: {
                moduleProps: this.props,
                className: moduleClassName
            },
            subscribeContainer: {
                className: 'ms-subscribe__details'
            },
            heading: subscribeHeading,
            text: subscribeParagraph,
            subscribeForm: {
                className: 'ms-subscribe__details__form',
                tag: 'form',
                'aria-label': this.props.resources.subscribeFormAriaLabel,
                name: 'subscribeForm',
                role: 'form',
                autoComplete: 'off'
            },
            emailInput: {
                className: 'ms-subscribe__details__form__email',
                tag: 'input',
                ref: this._emailAddressInputRef,
                type: 'email',
                'aria-label': this.props.resources.subscribeFormEmailAriaLabel,
                value: this.state.email,
                placeholder: this.props.resources.subscribeEmailPlaceholderText,
                onChange: this.handleChange('emailInput')
            },
            submitButton: {
                className: 'ms-subscribe__details__form__submit',
                tag: Button,
                'aria-label': this.props.resources.subscribeFormSubmitAriaLabel,
                type: 'submit',
                onClick: this._buttonSubscribe,
                title: this.props.resources.subscribeButtonLabel
            },
            submitButtonLabelText: this.props.resources.subscribeButtonLabel
        };

        return this.props.renderView(viewProps);
    }

    /**
     * Handle text change.
     * @param event - The dialog that is allowed to remain open.
     */
    public handleTextChange = (event: Msdyn365.ContentEditableEvent): void => {
        this.props.config.heading!.text = event.target.value;
    };

    /**
     * Handle paragraph change.
     * @param event - The dialog that is allowed to remain open.
     */
    public handleParagraphChange = (event: Msdyn365.ContentEditableEvent): void => {
        this.props.config.paragraph = event.target.value;
    };

    private handleChange = (inputText: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        switch (inputText) {
            case 'emailInput': {
                this.setState({ email: value });
                break;
            }
            default: {
                console.log('No input box changes');
            }
        }
    };

    private _buttonSubscribe(): void {
        this.setState(
            previousState => {
                return {
                    ...previousState
                };
            },
            () => {
                const emailAddress = this._emailAddressInputRef.current ? this._emailAddressInputRef.current.value : '';
                if (this.isEmailValid(emailAddress)) {
                    const request: SubscribeDataInput = new SubscribeDataInput(emailAddress);
                    subscribeAction(request, this.props.context.actionContext)
                        .then(result => {
                            this.setState({ email: '' });
                        })
                        .catch(error => {
                            this.props.telemetry.exception(error);
                            this.props.telemetry.debug('Failed to call async subscribe');
                        });
                }
            }
        );
    }

    private isEmailValid = (email: string): boolean => {
        const regex = /^(([^<>()\[\]\\.,;:\s@']+(\.[^<>()\[\]\\.,;:\s@']+)*)|('.+'))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return regex.test(email);
    };
}

export default Subscribe;
